/**
 * Who wrote each post.
 *
 * Separate from `posts.ts` so a person is described once rather than repeated
 * per article, and JSX-free for the same reason that file is: `src/seo/routes.ts`,
 * the feed builder and the sitemap script all import it under Node.
 *
 * Why a named person rather than the company. An article whose whole argument
 * is "here are the runs where our own product loses" is worth what the person
 * willing to sign it is worth, and `schema.org/Person` carries that in a way
 * `Organization` cannot — a company cannot have expertise, only people can.
 * The on-page rules in docs/marketing/content-seo.md ask for the byline; this
 * is where it comes from.
 */

export interface Author {
  /** Key referenced by `BlogPost.author`. Never change one after publication. */
  id: string;
  /** Full name, as it appears in the byline and in the article's JSON-LD. */
  name: string;
  /** The line under the name. A role, not a CV. */
  role: string;
  /**
   * One or two sentences for the card at the foot of an article. Claim only
   * what a reader could check — this page argues for honesty about numbers and
   * an inflated bio undoes that in one line.
   */
  bio: string;
  /**
   * Canonical URL for this person, used as the JSON-LD author `url` and as the
   * byline's link. Left unset until there is a real page or profile to point
   * at: an author link that 404s is worse than no link, and JSON-LD omits the
   * field rather than emitting an empty one.
   */
  url?: string;
  /**
   * Square portrait served from `web/public`. Optional — the byline falls back
   * to initials, which is better than a broken image on every article.
   */
  avatar?: string;
}

export const AUTHORS: Record<string, Author> = {
  pierre: {
    id: 'pierre',
    name: 'Pierre N.',
    role: 'Founder, VecTime Cloud',
    bio: 'Builds VecTime Cloud — the Go API and its metering, the Python inference service, and the benchmarks published here, including the runs that go against us.',
  },
};

/** The author of a post, or undefined if the id has no entry. */
export function findAuthor(id: string): Author | undefined {
  return AUTHORS[id];
}

/** `Pierre N.` → `PN`. The byline's fallback when there is no portrait. */
export function authorInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
